import {useMemberColumns} from "./member-columns.tsx"
import {Heading} from "@/components/tailwind/heading.tsx";
import {useAuth} from "@/context/use-auth.tsx";
import {useEffect, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {MemberListItem} from "@/models/member.ts"
import {getMembers} from "@/services/member-services.ts";
import {Button} from "@/components/tailwind/button.tsx";
import {useMembersTranslation} from "@/i18n/hooks";
import {DataTable} from "@/components/data-table.tsx";
import {Plus} from "lucide-react";
import MemberStatusFilter, {MemberStatusFilters} from "@/components/member-status-filter.tsx";
import log from '@/lib/logger'

export default function MemberListPage() {
    const { t } = useMembersTranslation()
    const {token} = useAuth()
    const [searchParams, setSearchParams] = useSearchParams()
    const columns = useMemberColumns()

    const [allMembers, setAllMembers] = useState<MemberListItem[]>([])
    const [filteredMembers, setFilteredMembers] = useState<MemberListItem[]>([])
    const [searchTerm, setSearchTerm] = useState("")
    const [statusFilters, setStatusFilters] = useState<MemberStatusFilters>({})
    const [urlParamsLoaded, setUrlParamsLoaded] = useState(false)

    // Read URL parameters on mount FIRST
    useEffect(() => {
        const urlSearchTerm = searchParams.get('search') || '';
        const status = searchParams.get('status');
        const missingConfidentiality = searchParams.get('missingConfidentiality') === 'true';
        const missingPrivacyConsent = searchParams.get('missingPrivacyConsent') === 'true';
        const missingMediaConsent = searchParams.get('missingMediaConsent') === 'true';

        setSearchTerm(urlSearchTerm);

        const newFilters: MemberStatusFilters = {};
        if (status === 'active') newFilters.showActive = true;
        if (status === 'passive') newFilters.showPassive = true;
        if (missingConfidentiality) newFilters.showMissingNDA = true;
        if (missingPrivacyConsent) newFilters.showMissingPrivacy = true;
        if (missingMediaConsent) newFilters.showMissingMedia = true;

        setStatusFilters(newFilters);
        setUrlParamsLoaded(true);
    }, [searchParams]);

    // Load members AFTER URL params are loaded
    useEffect(() => {
        if (!urlParamsLoaded) return;
        async function loadMembers() {
            try {
                // Build filters for backend API call
                let statusFilter: string | undefined;
                let missingConfidentiality: boolean | undefined;
                let missingPrivacyConsent: boolean | undefined;
                let missingMediaConsent: boolean | undefined;

                if (statusFilters.showActive) statusFilter = 'active';
                if (statusFilters.showPassive) statusFilter = 'passive';
                if (statusFilters.showMissingNDA) missingConfidentiality = true;
                if (statusFilters.showMissingPrivacy) missingPrivacyConsent = true;
                if (statusFilters.showMissingMedia) missingMediaConsent = true;

                const data = await getMembers(
                    token,
                    statusFilter,
                    missingConfidentiality,
                    missingPrivacyConsent,
                    missingMediaConsent
                );
                if (data) {
                    setAllMembers(data);
                }
            } catch (error) {
                log.error('Error loading members:', error);
            }
        }

        loadMembers();
    }, [token, statusFilters, urlParamsLoaded]);

    // Apply search filter only - backend already filtered by status
    useEffect(() => {
        let filtered = [...allMembers];

        // Apply search filter
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(member => {
                const memberNoMatch = member.memberNo.toString().includes(searchTerm);
                const nameMatch = `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchLower);
                const addressMatch = `${member.street} ${member.houseNumber} ${member.postcode} ${member.city}`.toLowerCase().includes(searchLower);
                const companyMatch = member.companyName?.toLowerCase().includes(searchLower);

                return memberNoMatch || nameMatch || addressMatch || companyMatch;
            });
        }

        setFilteredMembers(filtered);
    }, [allMembers, searchTerm]);

    // Update URL parameters when filters or search changes
    function updateURLParams(newSearchTerm: string, newFilters: MemberStatusFilters) {
        const params = new URLSearchParams();

        if (newSearchTerm.trim()) {
            params.set('search', newSearchTerm);
        }

        if (newFilters.showActive) {
            params.set('status', 'active');
        } else if (newFilters.showPassive) {
            params.set('status', 'passive');
        }

        if (newFilters.showMissingNDA) {
            params.set('missingConfidentiality', 'true');
        }

        setSearchParams(params);
    }

    function handleSearchChange(newSearchTerm: string) {
        setSearchTerm(newSearchTerm);
        updateURLParams(newSearchTerm, statusFilters);
    }

    function handleFilterChange(newFilters: MemberStatusFilters) {
        setStatusFilters(newFilters);
        updateURLParams(searchTerm, newFilters);
    }

    return (
        <div className="container mx-auto py-6">
            <div className="flex justify-between items-center mb-6">
                <Heading>{t("memberList.mainHeader")}</Heading>
                <Button
                    href="/members/new"
                    className="flex items-center"
                    data-testid="add-member-button"
                    aria-label={t("memberList.newMemberButton.ariaLabel")}
                >
                    <Plus className="h-4 w-4 mr-1"/>
                    {t("memberList.newMemberButton.title")}
                </Button>
            </div>

            <MemberStatusFilter
                searchTerm={searchTerm}
                filters={statusFilters}
                onSearchChange={handleSearchChange}
                onFilterChange={handleFilterChange}
            />

            <DataTable
                columns={columns}
                data={filteredMembers}
                resetPageKey={`${searchTerm}|${JSON.stringify(statusFilters)}`}
                data-testid="members-table"
            />
        </div>
    )
}
